/**
 * ErrorBoundary Component
 * 
 * A component that catches JavaScript errors anywhere in its child component tree
 * and displays a fallback UI instead of crashing the whole application.
 */
import { Component, ErrorInfo, ReactNode } from 'react'
import { cn, twStyles } from '../../utils/tailwind-utils'
import Button from '../ui/button'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback || <DefaultErrorFallback error={this.state.error} />
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: Error | null
  className?: string
}

function DefaultErrorFallback({ error, className }: ErrorFallbackProps) {
  return (
    <div className={cn('p-6 rounded-lg bg-card border border-border', className)}>
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth={1.5} 
            stroke="currentColor" 
            className="w-8 h-8 text-destructive"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" 
            />
          </svg>
        </div>
        <h2 className={cn(twStyles.typography.h2, 'mb-2')}>Something went wrong</h2>
        <p className={cn(twStyles.typography.p, 'mb-6')}>
          {error?.message || 'An unexpected error occurred'}
        </p>
        <Button onClick={() => window.location.reload()}>
          Refresh the page
        </Button>
      </div>
    </div>
  )
}

export default ErrorBoundary
